<template>
  <section
    class="section-card"
    :class="{ reverse }"
  >
    <BasePicture
      class="section-card__image"
      :src="getLocalizedImagePath(`/images/cards/${card.image}`)"
    />

    <BasePicture
      class="desktop section-card__image"
      :src="getLocalizedImagePath(`/images/cards/${card.image}`, 'png', '-', '-desktop')"
    />

    <div class="section-card__wrapper">
      <div class="section-card__header">
        <span class="section-card__board typography__label1">
          {{ $t("entities.sectionCards.createdInDonut") }}
        </span>

        <h3
          class="section-card__title typography__headline3"
          v-html="$t(card.title)"
        />
      </div>

      <p
        class="section-card__text typography__body1"
        v-html="$t(card.description)"
      />

      <BaseLink
        class="base-button-big base-button-second section-card__link"
        :href="card.href"
        target="_blank"
        @click="() => handleExampleClick(card.href)"
      >
        {{ $t("entities.sectionCards.viewExample") }}
      </BaseLink>
    </div>
  </section>
</template>

<script setup lang="ts">
interface Card {
  description: string;
  href: string;
  image: string;
  title: string;
}

interface Props {
  card: Card;
  reverse?: boolean;
}

defineProps<Props>();

const { sendEvent } = useAnalytics();
const { getLocalizedImagePath } = useLocalizedImage();

const handleExampleClick = (exampleLink: string) => {
  sendEvent("site_example_clicked", {
    example_link: exampleLink,
  });
};
</script>

<style scoped>
.section-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 24px;
  padding: 0 24px 40px;
  border-radius: 32px;
  background-color: var(--grey-7);
}

@media (--desktop) {
  .section-card {
    flex-direction: row;
    justify-content: space-between;
    padding: 0 80px 0 150px;
    border-radius: 50px;
  }

  .section-card.reverse {
    flex-direction: row-reverse;
    padding: 0 150px 0 80px;
  }
}

.section-card__image {
  flex-shrink: 0;
  width: 224px;
  height: 272px;
  margin-top: -10px;
}

.section-card__image.desktop {
  display: none;
}

@media (--desktop) {
  .section-card__image {
    display: none;
  }

  .section-card__image.desktop {
    display: flex;
    width: 300px;
    height: 360px;
    margin-top: -20px;
  }
}

@media (--desktop) {
  .reverse :deep(.section-card__image.section-card__image) {
    margin-left: auto;
  }
}

.section-card__wrapper {
  display: flex;
  flex-direction: column;
  row-gap: 12px;
}

@media (--desktop) {
  .section-card__wrapper {
    row-gap: 16px;
    max-width: 523px;
  }
}

.section-card__header {
  display: flex;
  flex-direction: column;
  row-gap: 6px;
}

@media (--desktop) {
  .section-card__header {
    row-gap: 8px;
  }
}

.section-card__board {
  width: max-content;
  padding: 4px 12px;
  border-radius: 8px;
  background-color: var(--orange-5);
}

@media (--desktop) {
  .section-card__title {
    font-weight: 400;
    font-size: 32px;
    line-height: 1.25;
  }
}

.section-card__text {
  color: var(--grey-1-5);
}

.section-card__link {
  align-self: flex-start;
  width: max-content;
  margin-top: 12px;
  border-radius: 20px;
}
</style>
